import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, ClipboardList, PackageSearch, Plus, RefreshCw, ShoppingCart, Truck, X } from 'lucide-react';
import { PO_STATUS, PR_STATUS, PROCUREMENT_STATUS, dateLabel } from '@adisys/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Kpi, KpiSkeleton, MetaBadge, Notice, StageBadge, errorText, inr2, qtyLabel } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { Column, DataTable, FilterBar, FilterSelect, SearchInput } from '../../components/DataTable';
import {
  Button, Checkbox, ErrorState, Field, Input, Modal, Select, Skeleton, Tabs, Textarea, cx, useToast,
} from '../../components/ui';

type TabKey = 'orders' | 'requests' | 'orders-po' | 'stock';

export function ProcurementPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  // 'requests' and 'orders-po' are the documented keys; accept 'po' as an alias.
  const tab = (raw === 'po' ? 'orders-po' : ['orders', 'requests', 'orders-po', 'stock'].includes(raw ?? '') ? raw : 'orders') as TabKey;
  const manage = can('crm.procurement.manage');
  const summary = useQuery({ queryKey: ['crm', 'procurement', 'summary'], queryFn: () => api.get('/crm/procurement/summary') });
  const s = summary.data;
  const [newRequest, setNewRequest] = useState(false);

  return (
    <>
      <PageHeader title="Procurement"
        description="Reserve stock for approved orders, buy what is missing, and receive it into the project."
        actions={manage && <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setNewRequest(true)}>New purchase request</Button>} />
      <div className="space-y-5 p-4 sm:p-6">
        {summary.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">{Array.from({ length: 5 }).map((_, i) => <KpiSkeleton key={i} />)}</div>
        ) : s && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi label="Open requests" value={String(s.openRequests)} icon={ClipboardList} tone={s.openRequests > 0 ? 'warning' : 'neutral'}
              sub="Waiting for a purchase order" to="?tab=requests" />
            <Kpi label="Awaiting vendor" value={String(s.awaitingVendor)} icon={ShoppingCart} sub="Issued, not yet confirmed" to="?tab=orders-po" />
            <Kpi label="In transit" value={String(s.inTransit)} icon={Truck} sub="Confirmed or partly received" to="?tab=orders-po" />
            <Kpi label="Late deliveries" value={String(s.lateDeliveries)} icon={AlarmClock} tone={s.lateDeliveries > 0 ? 'danger' : 'neutral'}
              sub="Past the promised date" to="?tab=orders-po" />
            <Kpi label="Low stock" value={String(s.lowStock)} icon={PackageSearch} tone={s.lowStock > 0 ? 'warning' : 'neutral'}
              sub="At or below reorder level" to="?tab=stock" />
          </div>
        )}

        <Tabs<TabKey> active={tab} onChange={(t) => setParams({ tab: t }, { replace: true })}
          tabs={[{ key: 'orders', label: 'Approved orders' }, { key: 'requests', label: 'Purchase requests' },
                 { key: 'orders-po', label: 'Purchase orders' }, { key: 'stock', label: 'Stock' }]} />

        {tab === 'orders' && <OrdersTab manage={manage} />}
        {tab === 'requests' && <RequestsTab manage={manage} />}
        {tab === 'orders-po' && <PurchaseOrdersTab onOpen={(id) => navigate(`/crm/procurement/purchase-orders/${id}`)} />}
        {tab === 'stock' && <StockTab canAdjust={manage || can('crm.catalog.manage')} />}
      </div>
      <NewRequestModal open={newRequest} onClose={() => setNewRequest(false)} />
    </>
  );
}

/* =================================================================== */
export function AllocationBar({ item }: { item: any }) {
  const qty = Number(item.quantity) || 1;
  const delivered = Number(item.deliveredQty) / qty * 100;
  const reserved = Number(item.reservedQty) / qty * 100;
  const onReq = Math.min(100 - delivered - reserved, Number(item.onRequest ?? 0) / qty * 100);
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100" title="Delivered · in stock for the order · on purchase request">
      <div className="flex h-full">
        <span className="bg-success" style={{ width: `${delivered}%` }} />
        <span className="bg-info" style={{ width: `${reserved}%` }} />
        <span className="bg-warning/60" style={{ width: `${Math.max(0, onReq)}%` }} />
      </div>
    </div>
  );
}

/** Full allocation picture for one order. Used in the desk's panel and in the project tab. */
export function OrderAllocation({ orderId, manage, onChanged }: { orderId: string; manage: boolean; onChanged?: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['crm', 'order', orderId], queryFn: () => api.get(`/crm/orders/${orderId}`) });
  const reserve = useMutation({
    mutationFn: () => api.post(`/crm/orders/${orderId}/reserve`),
    onSuccess: (r: any) => {
      toast.success('Stock re-checked', `${qtyLabel(r.reserved)} reserved, ${r.requested} line(s) requested.`);
      void qc.invalidateQueries({ queryKey: ['crm'] }); onChanged?.();
    },
    onError: (e) => toast.error('Could not re-check', errorText(e)),
  });
  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const o = q.data;
  const products = (o.items as any[]).filter((i) => i.kind === 'product');
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div><p className="font-semibold text-ink-900">{o.orderNo}</p>
          <p className="text-xs text-ink-500">{o.projectName} · {o.customerName}</p></div>
        <div className="flex items-center gap-2"><StageBadge stage={o.projectStage} />
          {manage && o.status === 'open' && (
            <Button size="sm" icon={<RefreshCw className="h-3.5 w-3.5" />} loading={reserve.isPending} onClick={() => reserve.mutate()}>Re-check stock & reserve</Button>)}</div>
      </div>
      <div className="overflow-x-auto rounded-lg ring-1 ring-line">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-sunken/60 text-xs text-ink-600">
            <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2 text-right">Ordered</th><th className="px-3 py-2 text-right">In stock for order</th>
              <th className="px-3 py-2 text-right">Delivered</th><th className="px-3 py-2 text-right">On request</th>
              <th className="px-3 py-2 text-right">Still to source</th><th className="w-32 px-3 py-2">Progress</th></tr>
          </thead>
          <tbody className="divide-y divide-line">
            {products.map((i) => {
              const short = Math.max(0, Number(i.quantity) - Number(i.reservedQty) - Number(i.deliveredQty) - Number(i.onRequest));
              return (
                <tr key={i.id}>
                  <td className="px-3 py-2"><p className="font-medium text-ink-900">{i.description}</p>
                    <p className="text-xs text-ink-500">{i.sku} · {qtyLabel(i.available)} free in warehouse</p></td>
                  <td className="tabular px-3 py-2 text-right">{qtyLabel(i.quantity)}</td>
                  <td className="tabular px-3 py-2 text-right">{qtyLabel(i.reservedQty)}</td>
                  <td className="tabular px-3 py-2 text-right">{qtyLabel(i.deliveredQty)}</td>
                  <td className="tabular px-3 py-2 text-right">{qtyLabel(i.onRequest)}</td>
                  <td className={cx('tabular px-3 py-2 text-right', short > 0 && 'font-semibold text-danger')}>{qtyLabel(short)}</td>
                  <td className="px-3 py-2"><AllocationBar item={i} /></td>
                </tr>);
            })}
            {products.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-ink-500">This order has no goods to source.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink-500">Bar: <span className="text-success">delivered</span> · <span className="text-info">in stock for the order</span> · <span className="text-warning">on a purchase request</span>.</p>
      {o.requests.length > 0 && (
        <p className="text-sm text-ink-700">Purchase requests: {o.requests.map((r: any, n: number) => (
          <span key={r.id}>{n > 0 && ', '}<Link className="text-brand-600 hover:underline" to="/crm/procurement?tab=requests">{r.prNo}</Link> <span className="text-xs text-ink-500">({r.status})</span></span>))}</p>)}
    </div>
  );
}

function OrdersTab({ manage }: { manage: boolean }) {
  const [q, setQ] = useState('');
  const [pending, setPending] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<any>(null);
  const query = useQuery({
    queryKey: ['crm', 'procurement', 'orders', { q, pending, page }],
    queryFn: () => api.get('/crm/procurement/orders', { q: q || undefined, pending: pending ? 'true' : undefined, page, size: 25 }),
  });
  const columns: Array<Column<any>> = [
    { key: 'order', header: 'Order', render: (o) => (<div><p className="font-medium text-ink-900">{o.orderNo}</p>
        <p className="text-xs text-ink-500">{o.projectName} · {o.customerName}</p></div>) },
    { key: 'stage', header: 'Stage', hideBelow: 'md', render: (o) => <StageBadge stage={o.projectStage} /> },
    { key: 'units', header: 'Units', numeric: true, render: (o) => qtyLabel(o.units) },
    { key: 'res', header: 'In stock for order', numeric: true, hideBelow: 'md', render: (o) => qtyLabel(o.reserved) },
    { key: 'del', header: 'Delivered', numeric: true, hideBelow: 'lg', render: (o) => qtyLabel(o.delivered) },
    { key: 'src', header: 'Still to source', numeric: true,
      render: (o) => <span className={cx(Number(o.toSource) > 0 && 'font-semibold text-danger')}>{qtyLabel(o.toSource)}</span> },
    { key: 'date', header: 'Approved', hideBelow: 'xl', render: (o) => dateLabel(o.createdAt) },
  ];
  return (
    <>
      <DataTable mobile="cards" columns={columns} rows={query.data?.data ?? []} rowKey={(o) => o.id} onRowClick={setOpen}
        loading={query.isLoading} error={query.error} onRetry={() => query.refetch()} page={query.data?.page} onPage={setPage}
        empty={{ icon: <Truck className="h-5 w-5" />, title: pending ? 'Everything is sourced' : 'No approved orders yet',
          description: 'Orders appear after a customer approves a quotation.' }}
        toolbar={<FilterBar><SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Order, project or customer" />
          <div className="flex h-9.5 items-center"><Checkbox label="Still to source only" checked={pending}
            onChange={(e) => { setPending(e.target.checked); setPage(1); }} /></div></FilterBar>} />
      <Modal open={!!open} onClose={() => setOpen(null)} size="xl" title="Order allocation" description="What is in stock, delivered and still to buy.">
        {open && <OrderAllocation orderId={open.id} manage={manage} />}
      </Modal>
    </>
  );
}

/* =================================================================== */
function RequestsTab({ manage }: { manage: boolean }) {
  const [status, setStatus] = useState('open');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['crm', 'procurement', 'requests', { status, page }],
    queryFn: () => api.get('/crm/procurement/requests', { status: status || undefined, page, size: 25 }),
  });
  const columns: Array<Column<any>> = [
    { key: 'no', header: 'Request', render: (r) => (<div><p className="font-medium text-ink-900">{r.prNo}</p>
        <p className="text-xs text-ink-500">{r.source === 'auto' ? 'Raised automatically' : `By ${r.requestedByName ?? 'staff'}`}</p></div>) },
    { key: 'proj', header: 'For', hideBelow: 'md', render: (r) => r.projectName ? `${r.projectName}${r.orderNo ? ` · ${r.orderNo}` : ''}` : 'Stock replenishment' },
    { key: 'lines', header: 'Lines', numeric: true, render: (r) => r.lineCount },
    { key: 'date', header: 'Raised', hideBelow: 'lg', render: (r) => dateLabel(r.createdAt) },
    { key: 'status', header: 'Status', render: (r) => <MetaBadge meta={PR_STATUS} value={r.status} /> },
  ];
  return (
    <>
      <DataTable mobile="cards" columns={columns} rows={query.data?.data ?? []} rowKey={(r) => r.id} onRowClick={(r) => setOpen(r.id)}
        loading={query.isLoading} error={query.error} onRetry={() => query.refetch()} page={query.data?.page} onPage={setPage}
        empty={{ icon: <ClipboardList className="h-5 w-5" />, title: 'No purchase requests', description: 'Shortfalls on approved orders create requests automatically.' }}
        toolbar={<FilterBar><FilterSelect label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} allLabel="All statuses"
          options={PR_STATUS.list.map((s) => ({ value: s.value, label: s.label }))} /></FilterBar>} />
      <RequestModal id={open} manage={manage} onClose={() => setOpen(null)} />
    </>
  );
}

function RequestModal({ id, manage, onClose }: { id: string | null; manage: boolean; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const req = useQuery({ queryKey: ['crm', 'procurement', 'request', id], enabled: !!id, queryFn: () => api.get(`/crm/procurement/requests/${id}`) });
  const vendors = useQuery({ queryKey: ['crm', 'vendors', 'active'], enabled: !!id && manage, queryFn: () => api.get('/crm/vendors', { active: 'true' }), staleTime: 60_000 });

  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [qty, setQty] = useState<Record<string, string>>({});
  const [cost, setCost] = useState<Record<string, string>>({});
  const [vendorId, setVendorId] = useState('');
  const [delivery, setDelivery] = useState('');
  const [terms, setTerms] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const items: any[] = req.data?.items ?? [];

  useEffect(() => {
    if (!req.data) return;
    const p: Record<string, boolean> = {}; const q: Record<string, string> = {};
    for (const i of req.data.items) { p[i.id] = Number(i.toOrder) > 0; q[i.id] = String(Number(i.toOrder)); }
    setPicked(p); setQty(q); setCost({}); setError(null); setCancelling(false); setReason('');
    setVendorId(req.data.items.find((i: any) => i.vendorId)?.vendorId ?? '');
  }, [req.data]);

  const chosen = items.filter((i) => picked[i.id] && Number(qty[i.id]) > 0);
  const create = useMutation({
    mutationFn: () => api.post('/crm/procurement/purchase-orders', {
      vendorId, expectedDelivery: delivery || undefined, paymentTerms: terms || undefined,
      items: chosen.map((i) => ({ prItemId: i.id, productId: i.productId, quantity: Number(qty[i.id]),
        ...(cost[i.id] ? { unitCost: Number(cost[i.id]) } : {}) })),
    }),
    onSuccess: (r: any) => {
      toast.success('Purchase order created', r.poNo); void qc.invalidateQueries({ queryKey: ['crm'] });
      onClose(); navigate(`/crm/procurement/purchase-orders/${r.id}`);
    },
    onError: (e) => { setError(e); toast.error('Could not create the purchase order', errorText(e)); },
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/crm/procurement/requests/${id}/cancel`, { reason }),
    onSuccess: () => { toast.success('Request cancelled'); void qc.invalidateQueries({ queryKey: ['crm'] }); onClose(); },
    onError: (e) => setError(e),
  });
  const fe = (k: string) => (error instanceof ApiRequestError ? error.fieldError(k) : undefined);
  const r = req.data;
  const canOrder = manage && r && ['open', 'ordered'].includes(r.status) && items.some((i) => Number(i.toOrder) > 0);

  return (
    <Modal open={!!id} onClose={onClose} size="xl" title={r ? `Purchase request ${r.prNo}` : 'Purchase request'}
      description={r ? (r.projectName ? `${r.projectName}${r.orderNo ? ` · ${r.orderNo}` : ''}` : 'Stock replenishment') : undefined}
      footer={r && (cancelling ? (
        <><Button onClick={() => setCancelling(false)}>Back</Button>
          <Button variant="danger" loading={cancel.isPending} disabled={reason.trim().length < 3} onClick={() => cancel.mutate()}>Cancel request</Button></>
      ) : (
        <>{manage && ['open', 'ordered'].includes(r.status) && <Button variant="ghost" onClick={() => { setError(null); setCancelling(true); }}>Cancel request</Button>}
          <Button onClick={onClose}>Close</Button>
          {canOrder && <Button variant="primary" icon={<ShoppingCart className="h-4 w-4" />} loading={create.isPending}
            disabled={!chosen.length || !vendorId} onClick={() => create.mutate()}>Create purchase order</Button>}</>
      ))}>
      {req.isLoading ? <Skeleton className="h-40 w-full" /> : req.isError ? <ErrorState error={req.error} onRetry={() => req.refetch()} /> : r && (
        <div className="space-y-4">
          {r.note && <p className="text-sm text-ink-600">{r.note}</p>}
          {cancelling ? (
            <Field label="Why is this request being cancelled?" required error={fe('reason')}>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
          ) : (
            <>
              {error instanceof ApiRequestError && <Notice tone="danger">{error.message}</Notice>}
              <div className="overflow-x-auto rounded-lg ring-1 ring-line">
                <table className="w-full min-w-[680px] text-left text-sm">
                  <thead className="bg-sunken/60 text-xs text-ink-600"><tr>
                    {canOrder && <th className="w-8 px-3 py-2" />}<th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2 text-right">Requested</th><th className="px-3 py-2 text-right">Ordered</th><th className="px-3 py-2 text-right">Received</th>
                    {canOrder && <><th className="w-24 px-3 py-2 text-right">Order now</th><th className="w-28 px-3 py-2 text-right">Unit cost</th></>}</tr></thead>
                  <tbody className="divide-y divide-line">
                    {items.map((i) => {
                      const left = Number(i.toOrder);
                      return (
                        <tr key={i.id}>
                          {canOrder && <td className="px-3 py-2"><input type="checkbox" disabled={left <= 0} checked={!!picked[i.id]} aria-label={`Select ${i.name}`}
                            onChange={(e) => setPicked({ ...picked, [i.id]: e.target.checked })} className="h-4 w-4 accent-[var(--color-brand-500)]" /></td>}
                          <td className="px-3 py-2"><p className="font-medium text-ink-900">{i.name}</p>
                            <p className="text-xs text-ink-500">{i.sku}{i.vendorName ? ` · usual vendor ${i.vendorName}` : ''}</p></td>
                          <td className="tabular px-3 py-2 text-right">{qtyLabel(i.quantity)}</td>
                          <td className="tabular px-3 py-2 text-right">{qtyLabel(i.orderedQty)}</td>
                          <td className="tabular px-3 py-2 text-right">{qtyLabel(i.receivedQty)}</td>
                          {canOrder && <>
                            <td className="px-3 py-2"><Input type="number" min={0} max={left} step="any" className="text-right" disabled={left <= 0}
                              aria-label={`Quantity of ${i.name}`} value={qty[i.id] ?? ''} onChange={(e) => setQty({ ...qty, [i.id]: e.target.value })} /></td>
                            <td className="px-3 py-2"><Input type="number" min={0} step="0.01" className="text-right" placeholder="Default" disabled={left <= 0}
                              aria-label={`Unit cost of ${i.name}`} value={cost[i.id] ?? ''} onChange={(e) => setCost({ ...cost, [i.id]: e.target.value })} /></td></>}
                        </tr>);
                    })}
                  </tbody>
                </table>
              </div>
              {canOrder && (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Vendor" required error={fe('vendorId')}>
                    <Select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                      <option value="">Choose a vendor…</option>
                      {(vendors.data?.data ?? []).map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </Select></Field>
                  <Field label="Expected delivery"><Input type="date" value={delivery} onChange={(e) => setDelivery(e.target.value)} /></Field>
                  <Field label="Payment terms" hint="Defaults to the vendor's terms."><Input value={terms} onChange={(e) => setTerms(e.target.value)} /></Field>
                </div>)}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function NewRequestModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<Array<{ productId: string; name: string; quantity: string }>>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const products = useQuery({
    queryKey: ['crm', 'products', 'picker', search], enabled: open,
    queryFn: () => api.get('/crm/products', { q: search || undefined, size: 20 }),
  });
  useEffect(() => { if (open) { setLines([]); setNote(''); setSearch(''); setError(null); } }, [open]);
  const save = useMutation({
    mutationFn: () => api.post('/crm/procurement/requests', {
      note: note || undefined, items: lines.map((l) => ({ productId: l.productId, quantity: Number(l.quantity) })) }),
    onSuccess: (r: any) => { toast.success('Purchase request raised'); void qc.invalidateQueries({ queryKey: ['crm'] }); onClose(); void r; },
    onError: (e) => setError(e),
  });
  return (
    <Modal open={open} onClose={onClose} size="lg" title="New purchase request" description="For stock replenishment. Shortfalls on customer orders are raised automatically."
      footer={<><Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} disabled={!lines.length || lines.some((l) => !(Number(l.quantity) > 0))} onClick={() => save.mutate()}>Raise request</Button></>}>
      <div className="space-y-4">
        {error instanceof ApiRequestError && <Notice tone="danger">{error.message}</Notice>}
        <Field label="Find a product"><SearchInput value={search} onChange={setSearch} placeholder="Name, SKU or model" className="w-full" /></Field>
        <ul className="max-h-40 divide-y divide-line overflow-y-auto rounded-lg ring-1 ring-line">
          {(products.data?.data ?? []).map((p: any) => (
            <li key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="min-w-0 truncate"><span className="font-medium text-ink-900">{p.name}</span> <span className="text-xs text-ink-500">{p.sku} · {qtyLabel(p.available)} free</span></span>
              <Button size="sm" disabled={lines.some((l) => l.productId === p.id)}
                onClick={() => setLines([...lines, { productId: p.id, name: p.name, quantity: '1' }])}>Add</Button>
            </li>))}
          {products.isLoading && <li className="p-3"><Skeleton className="h-5 w-full" /></li>}
        </ul>
        {lines.length > 0 && (
          <ul className="space-y-2">
            {lines.map((l, n) => (
              <li key={l.productId} className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{l.name}</span>
                <Input type="number" min={0} step="any" className="w-24 text-right" aria-label={`Quantity of ${l.name}`} value={l.quantity}
                  onChange={(e) => setLines(lines.map((x, k) => (k === n ? { ...x, quantity: e.target.value } : x)))} />
                <button aria-label={`Remove ${l.name}`} className="rounded p-1 text-ink-400 hover:text-ink-700" onClick={() => setLines(lines.filter((_, k) => k !== n))}><X className="h-4 w-4" /></button>
              </li>))}
          </ul>)}
        <Field label="Note"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

/* =================================================================== */
function PurchaseOrdersTab({ onOpen }: { onOpen: (id: string) => void }) {
  const [status, setStatus] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [late, setLate] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const vendors = useQuery({ queryKey: ['crm', 'vendors', 'all'], queryFn: () => api.get('/crm/vendors'), staleTime: 60_000 });
  const query = useQuery({
    queryKey: ['crm', 'procurement', 'pos', { status, vendorId, late, q, page }],
    queryFn: () => api.get('/crm/procurement/purchase-orders', { status: status || undefined, vendorId: vendorId || undefined, late: late ? 'true' : undefined, q: q || undefined, page, size: 25 }),
  });
  const columns: Array<Column<any>> = [
    { key: 'po', header: 'Purchase order', render: (p) => (<div><p className="font-medium text-ink-900">{p.poNo}</p>
        <p className="text-xs text-ink-500">{p.projectName ?? 'Stock replenishment'}</p></div>) },
    { key: 'vendor', header: 'Vendor', render: (p) => p.vendorName },
    { key: 'recv', header: 'Received', numeric: true, hideBelow: 'md', render: (p) => `${qtyLabel(p.unitsReceived)} / ${qtyLabel(p.unitsOrdered)}` },
    { key: 'eta', header: 'Expected', hideBelow: 'md', render: (p) => (
        <span className={cx(p.isLate && 'font-medium text-danger')}>{p.expectedDelivery ? dateLabel(p.expectedDelivery) : '—'}{p.isLate && <span className="block text-xs">Late</span>}</span>) },
    { key: 'total', header: 'Value', numeric: true, hideBelow: 'lg', render: (p) => inr2(p.grandTotal) },
    { key: 'status', header: 'Status', render: (p) => <MetaBadge meta={PO_STATUS} value={p.status} /> },
  ];
  return (
    <DataTable mobile="cards" columns={columns} rows={query.data?.data ?? []} rowKey={(p) => p.id} onRowClick={(p) => onOpen(p.id)}
      loading={query.isLoading} error={query.error} onRetry={() => query.refetch()} page={query.data?.page} onPage={setPage}
      empty={{ icon: <ShoppingCart className="h-5 w-5" />, title: 'No purchase orders', description: 'Open a purchase request to place an order with a vendor.' }}
      toolbar={<FilterBar active={[status, vendorId, q].filter(Boolean).length + (late ? 1 : 0)}
        onReset={() => { setStatus(''); setVendorId(''); setQ(''); setLate(false); setPage(1); }}>
        <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="PO, vendor or project" />
        <FilterSelect label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} allLabel="All statuses"
          options={PO_STATUS.list.map((s) => ({ value: s.value, label: s.label }))} />
        <FilterSelect label="Vendor" value={vendorId} onChange={(v) => { setVendorId(v); setPage(1); }} allLabel="All vendors"
          options={(vendors.data?.data ?? []).map((v: any) => ({ value: v.id, label: v.name }))} />
        <div className="flex h-9.5 items-center"><Checkbox label="Late only" checked={late} onChange={(e) => { setLate(e.target.checked); setPage(1); }} /></div>
      </FilterBar>} />
  );
}

/* =================================================================== */
function StockTab({ canAdjust }: { canAdjust: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [adjust, setAdjust] = useState<any>(null);
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);

  // With no filter, show what needs attention: low and out of stock.
  const statuses = status ? [status] : ['low_stock', 'out_of_stock'];
  const query = useQuery({
    queryKey: ['crm', 'procurement', 'stock', { status, q, page }],
    queryFn: async () => {
      if (statuses.length === 1) return api.get('/crm/products', { stockStatus: statuses[0], q: q || undefined, page, size: 25 });
      const [a, b] = await Promise.all(statuses.map((s) => api.get('/crm/products', { stockStatus: s, q: q || undefined, size: 100 })));
      const data = [...b.data, ...a.data];
      return { data, page: { number: 1, size: data.length, total: data.length, totalPages: 1 }, showsCost: a.showsCost };
    },
  });
  const showsCost = !!query.data?.showsCost;
  const save = useMutation({
    mutationFn: () => api.post(`/crm/products/${adjust.id}/stock-adjustment`, { delta: Number(delta), reason }),
    onSuccess: () => { toast.success('Stock adjusted'); setAdjust(null); void qc.invalidateQueries({ queryKey: ['crm'] }); },
    onError: (e) => setError(e),
  });
  const fe = (k: string) => (error instanceof ApiRequestError ? error.fieldError(k) : undefined);

  const columns: Array<Column<any>> = [
    { key: 'p', header: 'Product', render: (p) => (<div><p className="font-medium text-ink-900">{p.name}</p>
        <p className="text-xs text-ink-500">{p.sku}{p.brandName ? ` · ${p.brandName}` : ''}</p></div>) },
    { key: 'hand', header: 'On hand', numeric: true, render: (p) => qtyLabel(p.stockOnHand) },
    { key: 'res', header: 'Reserved', numeric: true, hideBelow: 'md', render: (p) => qtyLabel(p.stockReserved) },
    { key: 'av', header: 'Available', numeric: true, render: (p) => <span className="font-medium text-ink-900">{qtyLabel(p.available)}</span> },
    { key: 'ro', header: 'Reorder at', numeric: true, hideBelow: 'lg', render: (p) => qtyLabel(p.reorderLevel) },
    ...(showsCost ? [{ key: 'cost', header: 'Cost', numeric: true, hideBelow: 'xl' as const, render: (p: any) => inr2(p.costPrice) }] : []),
    { key: 'st', header: 'Status', render: (p) => <MetaBadge meta={PROCUREMENT_STATUS} value={p.procurementStatus} /> },
    { key: 'a', header: '', render: (p) => canAdjust && (
        <div className="text-right" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" onClick={() => { setAdjust(p); setDelta(''); setReason(''); setError(null); }}>Adjust</Button></div>) },
  ];
  return (
    <>
      <DataTable mobile="cards" columns={columns} rows={query.data?.data ?? []} rowKey={(p) => p.id}
        loading={query.isLoading} error={query.error} onRetry={() => query.refetch()} page={statuses.length === 1 ? query.data?.page : undefined} onPage={setPage}
        empty={{ icon: <PackageSearch className="h-5 w-5" />, title: 'Nothing needs attention', description: 'No product is low, out of stock or on order.' }}
        toolbar={<FilterBar><SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Product or SKU" />
          <FilterSelect label="Stock status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} allLabel="Low & out of stock"
            options={[{ value: 'low_stock', label: 'Low stock' }, { value: 'out_of_stock', label: 'Out of stock' }, { value: 'on_order', label: 'On order' }, { value: 'in_stock', label: 'In stock' }]} /></FilterBar>} />
      <Modal open={!!adjust} onClose={() => setAdjust(null)} size="sm" title="Adjust stock" description={adjust ? `${adjust.name} · ${qtyLabel(adjust.stockOnHand)} on hand` : undefined}
        footer={<><Button onClick={() => setAdjust(null)}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!Number(delta) || reason.trim().length < 3} onClick={() => save.mutate()}>Save adjustment</Button></>}>
        <div className="space-y-3">
          {error instanceof ApiRequestError && !fe('delta') && !fe('reason') && <Notice tone="danger">{error.message}</Notice>}
          <Field label="Change (use − to remove stock)" required error={fe('delta')}>
            <Input type="number" step="any" value={delta} onChange={(e) => setDelta(e.target.value)} /></Field>
          <Field label="Reason" required error={fe('reason')} hint="Stock count, damage, opening balance…">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        </div>
      </Modal>
    </>
  );
}
